<template>
    <div class="miniMatchCard miniMatchCardRunning">
        <div class="miniMatchCard_teamInfo">
            <div class="miniMatchCard_dateInfo">
                <Badge
                    v-if="runningEvent.isLive"
                    text="LIVE"
                    variant="solid"
                    color="live"
                    size="sm"
                />
                <template v-if="runningEvent.isLive">
                    <span class="miniMatchCard_time" />{{ displayScore?.periodText }}
                    <span
                        v-if="_isShowInjuryTime"
                    >
                        +{{ runningEvent.extraInfo?.injuryTime }}'
                    </span>
                </template>

                <span
                    v-else
                    class="miniMatchCard_time"
                >
                    {{ dateDisplay }}
                    {{ timeDisplay }}
                </span>
            </div>
            <div class="miniMatchCard_teams">
                <div class="miniMatchCard_teamRow">
                    <div class="miniMatchCard_teamNameContainer">
                        <picture>
                            <source
                                v-bind:srcset="`${homeTeamImage.webp}`"
                                type="image/webp"
                                class="miniMatchCard_flag"
                            />
                            <img
                                v-bind:src="`${homeTeamImage.origin}`"
                                alt=""
                                aria-hidden="true"
                                class="miniMatchCard_flag"
                            />
                        </picture>
                        <div
                            class="miniMatchCard_teamName"
                            v-bind:class="{ 'miniMatchCard_teamName-favorite': runningEvent.homeTeam?.isFavorite }"
                        >
                            {{ homeName }}
                        </div>
                    </div>
                    <div class="badge">
                        <FoulCard v-bind:count="homeCardCount" />
                    </div>
                    <div v-if="runningEvent.isLive && displayScore?.score" class="miniMatchCard_score">
                        {{ displayScore?.score?.home }}
                    </div>
                </div>
                <div class="miniMatchCard_teamRow">
                    <div class="miniMatchCard_teamNameContainer">
                        <picture>
                            <source
                                v-bind:srcset="`${awayTeamImage.webp}`"
                                type="image/webp"
                                class="miniMatchCard_flag"
                            />
                            <img
                                v-bind:src="`${awayTeamImage.origin}`"
                                alt=""
                                aria-hidden="true"
                                class="miniMatchCard_flag"
                            />
                        </picture>
                        <div
                            class="miniMatchCard_teamName"
                            v-bind:class="{ 'miniMatchCard_teamName-favorite': runningEvent.awayTeam?.isFavorite }"
                        >
                            {{ awayName }}
                        </div>
                    </div>
                    <div class="badge">
                        <FoulCard v-bind:count="awayCardCount" />
                    </div>
                    <div v-if="runningEvent.isLive && displayScore?.score" class="miniMatchCard_score">
                        {{ displayScore?.score?.away }}
                    </div>
                </div>
            </div>
        </div>

        <div v-bind:class="['miniMatchCard_oddsInfo', { 'miniMatchCard_oddsInfo-live': runningEvent.isLive }]">
            <div class="miniMatchCard_marketLabel">
                {{ fullTimeHandicapText }}
            </div>
            <StaticSpinner v-if="!oddsLoaded" />
            <div v-else-if="handicapOdds" class="miniMatchCard_odds">
                <TournamentSingleOdds
                    v-for="price in handicapOdds.prices"
                    v-bind:key="`${handicapOdds.id}-${price.option}`"
                    layout="vertical"
                    v-bind:odds="handicapOdds"
                    v-bind:price="price"
                    v-bind:event="runningEvent"
                    v-bind:displaySection="DisplaySection.TournamentCenter_Knockout"
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { storeToRefs } from 'pinia';
    import { computed, ref, toRefs } from 'vue';
    import StaticSpinner from '@/components/common/StaticSpinner.vue';
    import Badge from '@/components/common/badge/Badge.vue';
    import FoulCard from '@/components/common/foulCard/FoulCard.vue';
    import { isShowInjuryTime } from '@/components/oddsDisplay/matchDisplay/useMatchDisplay';
    import TournamentSingleOdds from '@/components/tournamentCenter/TournamentSingleOdds.vue';
    import { getTournamentCenterTeamImage } from '@/components/tournamentCenter/tournamentCenterHelper';
    import { useLiveScoreDisplay } from '@/composable/useLiveScoreDisplay';
    import { useLiveScoreRestriction } from '@/composable/useLiveScoreRestriction';
    import { formatDate } from '@/core/lib/date';
    import { i18n } from '@/core/lib/i18n';
    import { sortOdds } from '@/core/lib/oddsHelper';
    import { getTranslated } from '@/core/lib/translationHelper';
    import { useEventOdds } from '@/core/oddsApi/composable/useEventOdds';
    import { useLiveScore } from '@/core/oddsApi/composable/useLiveScore';
    import { EnumOddsCategoryType, EnumPresetFilterType } from '@/core/oddsApi/oddsApiType';
    import { BetTypeGroup, DisplaySection, MarketType, SiteStyle } from '@/interface/enum';
    import type { ITournamentEvent } from '@/interface/tournamentCenter';
    import { useCustomerStore } from '@/store/customerStore';

    const props = defineProps<{
        runningEvent: ITournamentEvent;
    }>();

    const { runningEvent } = toRefs(props);

    const { isSupported } = useLiveScoreRestriction(runningEvent);

    const eventId = computed(() => runningEvent.value.id);
    const { liveScore: providerLiveScore } = useLiveScore(eventId, isSupported);
    const { displayScore } = useLiveScoreDisplay(runningEvent, providerLiveScore, ref(null), SiteStyle.Asia, ref(true));

    const homeTeamImage = computed(() => getTournamentCenterTeamImage(runningEvent.value.homeTeam?.countryName));
    const awayTeamImage = computed(() => getTournamentCenterTeamImage(runningEvent.value.awayTeam?.countryName));
    const homeName = computed(() => getTranslated(runningEvent.value.homeTeam?.name));
    const awayName = computed(() => getTranslated(runningEvent.value.awayTeam?.name));
    const dateDisplay = computed(() => (runningEvent.value.showTime ? formatDate(runningEvent.value.showTime, 'MM/dd') : ''));
    const timeDisplay = computed(() => (runningEvent.value.showTime ? formatDate(runningEvent.value.showTime, 'HH:mm') : ''));
    const _isShowInjuryTime = computed(() => isShowInjuryTime(runningEvent.value));
    const awayCardCount = computed(() => runningEvent.value.awayTeam?.redCard ?? 0);
    const homeCardCount = computed(() => runningEvent.value.homeTeam?.redCard ?? 0);
    const fullTimeHandicapText = computed(() => i18n.t('full_time_handicap_short'));

    const { priceStyle } = storeToRefs(useCustomerStore());

    const { oddsList, loaded: oddsLoaded } = useEventOdds({
        eventId: runningEvent.value.id,
        presetFilter: runningEvent.value.isLive ? EnumPresetFilterType.Live : EnumPresetFilterType.NonLive,
        oddsCategory: EnumOddsCategoryType.All,
        priceStyle,
        sportType: runningEvent.value.sportType,
        leagueSize: runningEvent.value.league.size,
        countBehavior: true,
        marketGroupIds: [BetTypeGroup.Main],
        marketTypes: [MarketType.Handicap],
    });

    const handicapOdds = computed(() => sortOdds(oddsList.value, runningEvent.value.sportType)[0]);
</script>

<style lang="scss" scoped>
    @import "@/components/tournamentCenter/MiniMatchCard";
</style>
